/**
 * Sprint 15 Ola 7 — AuriaContextProvider
 * Global React context for aurIA FAB conversacional.
 * Exports: AuriaContextProvider, useAuriaContext
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
// MACRO 1 · Catarsis 1 · cicatriz #3: el value del Provider se valida contra
// su contrato. Si un refactor elimina un método (como pasó con setIsOpen),
// el Provider lanza EN EL ACTO — no 5 páginas mudas con ?.
import { AriaContextSchema, validateContract } from '../../contracts'

const AuriaContext = createContext(null)

export function AuriaContextProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false)
  const [pendingContext, setPendingContext] = useState(null)
  const [activeCommand, setActiveCommandState] = useState(null)
  const [conversationHistory, setConversationHistory] = useState([])
  const [userId, setUserId] = useState(null)

  // Load userId from Supabase session on mount
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUserId(data?.session?.user?.id || null)
    }).catch(() => {
      setUserId(null)
    })
  }, [])

  const openSheet = useCallback(() => {
    // Read pending context from localStorage and clear it
    try {
      const raw = localStorage.getItem('prime_auria_pending_context')
      if (raw) {
        const parsed = JSON.parse(raw)
        setPendingContext(parsed)
        localStorage.removeItem('prime_auria_pending_context')
      } else {
        setPendingContext(null)
      }
    } catch {
      setPendingContext(null)
    }
    setIsOpen(true)
  }, [])

  const closeSheet = useCallback(() => {
    setIsOpen(false)
  }, [])

  const setActiveCommand = useCallback((cmd) => {
    setActiveCommandState(cmd)
  }, [])

  const clearActiveCommand = useCallback(() => {
    setActiveCommandState(null)
  }, [])

  const appendMessage = useCallback(({ role, content, context_hint, command }) => {
    setConversationHistory((prev) => [
      ...prev,
      { role, content, context_hint: context_hint || null, command: command || null },
    ])
  }, [])

  const value = {
    isOpen,
    // Alias setIsOpen → openSheet para las 5 páginas V2 · no se expone
    // setIsOpen(false) directamente para no saltar el flujo del openSheet
    setIsOpen: (open) => { if (open) openSheet(); else closeSheet() },
    pendingContext,
    activeCommand,
    conversationHistory,
    userId,
    openSheet,
    closeSheet,
    setActiveCommand,
    clearActiveCommand,
    appendMessage,
    setConversationHistory,
  }

  // Contrato AriaContext: fail-loud. Una violación aquí es un bug de
  // programación (no de datos) — lanzar es lo correcto y lo ruidoso.
  const check = validateContract(AriaContextSchema, value, {
    contract: 'AriaContext',
    source: 'AuriaContextProvider value',
  })
  if (!check.ok) {
    throw new Error('[AriaContext] el value del Provider viola su contrato — ver consola/Sentry')
  }

  return <AuriaContext.Provider value={value}>{children}</AuriaContext.Provider>
}

export function useAuriaContext() {
  const ctx = useContext(AuriaContext)
  if (!ctx) {
    throw new Error('useAuriaContext must be used within AuriaContextProvider')
  }
  return ctx
}

export default AuriaContextProvider
